import classNames from "classnames";
import {useFormContext} from "react-hook-form";

import layoutPresetCompact from "@/assets/images/statuspages/layout-preset-compact.svg";
import layoutPresetExpanded from "@/assets/images/statuspages/layout-preset-expanded.svg";
import layoutPresetMinimal from "@/assets/images/statuspages/layout-preset-minimal.svg";
import {Card} from "@/components/card/Card.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {ColorPickerField} from "@/components/form/fields/ColorPickerField/ColorPickerField.tsx";
import {ImageInputField} from "@/components/form/fields/ImageInputField/ImageInputField.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {TextAreaField} from "@/components/form/fields/TextAreaField.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {RequiredFieldsNote} from "@/components/form/requiredFieldsNote/RequiredFieldsNote.tsx";
import {Section} from "@/components/section/Section.tsx";
import {SelectionCard} from "@/components/selectionCard/SelectionCard.tsx";
import {ToggleButton} from "@/components/toggleButton/ToggleButton.tsx";
import {
  STATUS_PAGE_BRAND_AUTOFILL,
  STATUS_PAGE_DEFAULT_STATUS_DATE_RANGE,
  STATUS_PAGE_LAYOUT_DISPLAY,
} from "@/lib/featureFlags.ts";
import {StatusPageLayoutPreset, StatusPageTheme} from "@/lib/types/statuspages.ts";
import {DesignPreviewPane} from "@/pages/statuspages/statuspages-manage/components/DesignPreviewPane.tsx";
import {BrandAutofillField} from "@/pages/statuspages/tabs/design/BrandAutofill/BrandAutofillField.tsx";
import {StatusPageDesignTabProps} from "@/pages/statuspages/tabs/design/StatusPageDesignTab.types.ts";

import styles from "./StatusPageDesignTabV2.module.scss";

export const StatusPageDesignTabV2 = ({default_theme}: StatusPageDesignTabProps) => {
  const {user, userHasFeature} = useCurrentUserContext();
  const {has_advanced_features} = user || {has_advanced_features: false};
  const showStatusDateRangeField = userHasFeature(STATUS_PAGE_DEFAULT_STATUS_DATE_RANGE);
  const showLayoutDisplaySection = userHasFeature(STATUS_PAGE_LAYOUT_DISPLAY);
  const showBrandAutofill = userHasFeature(STATUS_PAGE_BRAND_AUTOFILL);
  const {watch, setValue} = useFormContext();
  const theme = watch("theme");
  const showStatusTab = watch("show_status_tab");
  const showHistoryTab = watch("show_history_tab");
  const layoutPreset = watch("layout_preset");
  const isCompactLayout = showLayoutDisplaySection && layoutPreset === StatusPageLayoutPreset.COMPACT;
  const isMinimalLayout = showLayoutDisplaySection && layoutPreset === StatusPageLayoutPreset.MINIMAL;
  const isExpandedLayout = showLayoutDisplaySection && layoutPreset === StatusPageLayoutPreset.EXPANDED;

  return (
    <div className={styles.twoColumn}>
      <div className={styles.mainColumn}>
        <RequiredFieldsNote spacing={"none"} />

        <div className={"d-flex flex-column gap-1"}>
          <span className={"pulseTextBodyBold"}>Branding</span>
          <span className={"pulseTextBodySmall"}>Customize your Status Page's logo, colors, and visual style</span>
        </div>

        <div>
          <div className={"d-flex flex-column gap-3"}>
            {showBrandAutofill && <BrandAutofillField />}
            <ImageInputField
              name={"logo"}
              label={"Custom Logo"}
              helpText={"Max height 200px. Horizontal PNG or JPG."}
            />
            <ImageInputField
              name={"favicon"}
              label={"Custom Favicon"}
              helpText={"Minimum 200×200px. Square PNG or JPG."}
            />
            {!showBrandAutofill && (
              <InputField name={"company_website_url"} label={"Website URL"} helpText={"Your company's website/URL."} />
            )}

            {default_theme === StatusPageTheme.LEGACY && (
              <div className={"d-flex flex-column gap-1"}>
                <FormLabel forId={"theme"}>Theme</FormLabel>
                <ToggleButton
                  items={[
                    {id: StatusPageTheme.LEGACY, label: "Legacy"},
                    {id: StatusPageTheme.INSPIRE, label: "Inspire"},
                  ]}
                  selectedId={theme}
                  onChange={(id) => setValue("theme", id)}
                  className={"w-fit-content"}
                />
              </div>
            )}

            {theme === StatusPageTheme.INSPIRE && (
              <div className={styles.twoInputs}>
                <ColorPickerField
                  name={"custom_header_bg_color_hex"}
                  label="Header Background Color"
                  hasTextInput={true}
                />
                <ColorPickerField name={"custom_header_text_color_hex"} label="Header Text Color" hasTextInput={true} />
              </div>
            )}
          </div>
        </div>

        {showLayoutDisplaySection && (
          <Section title="layout & display" description="Control the visual density and component display style">
            <div className={"d-flex flex-column gap-4"}>
              <div className={"d-flex flex-column gap-2"}>
                <FormLabel forId={"layout_preset"}>Layout Preset</FormLabel>
                <SelectionCard
                  id={"layout_preset"}
                  value={layoutPreset}
                  onChange={(value) => setValue("layout_preset", value, {shouldDirty: true})}
                  layout={"horizontal"}
                >
                  <SelectionCard.Option
                    value={StatusPageLayoutPreset.EXPANDED}
                    title={"Expanded"}
                    description={"Single column, large cards"}
                    imageSrc={layoutPresetExpanded}
                  />
                  <SelectionCard.Option
                    value={StatusPageLayoutPreset.COMPACT}
                    title={"Compact"}
                    description={"Two-column, condensed cards"}
                    imageSrc={layoutPresetCompact}
                  />
                  <SelectionCard.Option
                    value={StatusPageLayoutPreset.MINIMAL}
                    title={"Minimal"}
                    description={"Two-column, minimal cards"}
                    imageSrc={layoutPresetMinimal}
                  />
                </SelectionCard>
              </div>
              <Card variant={"white"} className={"d-flex flex-column gap-3"}>
                <SwitchField
                  name={"show_component_bars"}
                  label={"Show Uptime bars on component cards"}
                  description={"Display Uptime bar on each top-level component card."}
                  labelPosition={"left"}
                  disabled={isExpandedLayout || isMinimalLayout}
                  tooltip={
                    isExpandedLayout
                      ? "Full Uptime bars are always shown in expanded component card view."
                      : isMinimalLayout
                        ? "Uptime bars are not available in minimal layout."
                        : undefined
                  }
                />
                <Divider color={"subMenu"} />
                <SwitchField
                  name={"show_component_descriptions"}
                  label={"Show component descriptions"}
                  description={"Show description text under component and group names."}
                  labelPosition={"left"}
                />
              </Card>
            </div>
          </Section>
        )}

        <Section
          title="page content"
          description="Control which sections and data are visible to visitors on your Status Page"
        >
          <div className={"d-flex flex-column gap-4"}>
            <Card variant={"white"} className={"d-flex flex-column gap-4"}>
              <SwitchField
                name={"show_status_tab"}
                label={"Display Current Status tab"}
                description={"Display when there is data available."}
                labelPosition={"left"}
              />
              {showStatusTab && (
                <>
                  <Divider />
                  <div className={classNames(styles.switchGroupNested, "d-flex flex-column gap-3")}>
                    <SwitchField
                      name={"show_active_incidents"}
                      label={"Display Active Incidents & Maintenance"}
                      labelPosition={"left"}
                      labelTypography={"bodySmall"}
                    />
                    {showLayoutDisplaySection && (
                      <>
                        <Divider color={"subMenu"} />
                        <SwitchField
                          name={"hide_empty_tabs_status"}
                          label={"Automatically hide sub-tabs with no events"}
                          labelPosition={"left"}
                          labelTypography={"bodySmall"}
                        />
                      </>
                    )}
                    {showStatusDateRangeField && (
                      <>
                        <Divider color={"subMenu"} />
                        <InputField
                          className={styles.historyRange}
                          name={"default_status_date_range"}
                          label={"Default current status range (days)"}
                          helpText={"Max. 90 days."}
                          labelTypography={"bodySmall"}
                          required
                        />
                      </>
                    )}
                    <Divider color={"subMenu"} />
                    <SwitchField
                      name={"show_component_response_time"}
                      label={"Display Response Time graph per component"}
                      description={"Display in the Current Status table."}
                      labelPosition={"left"}
                      labelTypography={"bodySmall"}
                      disabled={isCompactLayout || isMinimalLayout}
                      tooltip={
                        isCompactLayout || isMinimalLayout
                          ? "Response Time graphs are unavailable in compact or minimal layout."
                          : undefined
                      }
                    />
                  </div>
                </>
              )}
            </Card>

            <Card variant={"white"} className={"d-flex flex-column gap-4"}>
              <SwitchField
                name={"show_history_tab"}
                label={"Display History & Incidents tab"}
                description={"Display when there is data available."}
                labelPosition={"left"}
              />
              {showHistoryTab && (
                <>
                  <Divider />
                  <div className={classNames(styles.switchGroupNested, "d-flex flex-column gap-3")}>
                    {showLayoutDisplaySection && (
                      <>
                        <SwitchField
                          name={"hide_empty_tabs_history"}
                          label={"Automatically hide sub-tabs with no events"}
                          labelPosition={"left"}
                          labelTypography={"bodySmall"}
                        />
                        <Divider color={"subMenu"} />
                      </>
                    )}
                    <InputField
                      className={styles.historyRange}
                      name={"default_history_date_range"}
                      label={"Default history range (days)"}
                      helpText={"Max. 90 days."}
                      labelTypography={"bodySmall"}
                      required
                    />
                    <Divider color={"subMenu"} />
                    <SwitchField
                      name={"show_past_incidents"}
                      label={"Past Incidents"}
                      description={"Display past Incidents under History."}
                      labelPosition={"left"}
                      labelTypography={"bodySmall"}
                    />
                    <Divider color={"subMenu"} />
                    <SwitchField
                      name={"show_history_snake"}
                      label={"History graph (All Components)"}
                      description={"Display a graph visual of your components' availability history."}
                      labelPosition={"left"}
                    />
                    <Divider color={"subMenu"} />
                    <SwitchField
                      name={"show_component_history"}
                      label={"Component history table"}
                      description={"Display a table of your components' historical metrics."}
                      labelPosition={"left"}
                      labelTypography={"bodySmall"}
                    />
                    <Divider color={"subMenu"} />
                    <SwitchField
                      name={"show_summary_metrics"}
                      label={"Summary metrics"}
                      description={"Global uptime, outages and total downtime."}
                      labelPosition={"left"}
                      labelTypography={"bodySmall"}
                    />
                  </div>
                </>
              )}
            </Card>

            <Card variant={"white"}>
              <SwitchField
                name={"allow_pdf_report"}
                label={"PDF report download"}
                description={"Allow visitors to download a PDF status report."}
                labelPosition={"left"}
              />
            </Card>

            {showLayoutDisplaySection && (
              <Card variant={"white"}>
                <SwitchField
                  name={"show_overall_status"}
                  label={"Overall status banner"}
                  description={'Show the "All Systems Operational" status banner at the top of the page.'}
                  labelPosition={"left"}
                />
              </Card>
            )}

            {showLayoutDisplaySection && (
              <Card variant={"white"}>
                <SwitchField
                  name={"show_branding_footer"}
                  label={"Uptime.com branding"}
                  description={'Show "Powered by Uptime.com" in the page footer.'}
                  labelPosition={"left"}
                />
              </Card>
            )}
          </div>
        </Section>

        <Section title="custom code" description="CSS, HTML overrides and email branding">
          <div className={"d-flex flex-column gap-4"}>
            {has_advanced_features && (
              <>
                {theme === StatusPageTheme.INSPIRE && (
                  <>
                    <TextAreaField
                      name={"custom_css_inspire"}
                      label={"Custom CSS"}
                      placeholder={"/* Add custom styles here */"}
                      tooltip={"OPTIONAL: Inline CSS used to style customized HTML in the Header and Footer sections."}
                    />
                    <TextAreaField
                      name={"custom_header_html_inspire"}
                      label={"Custom Header HTML"}
                      placeholder={"<div>Custom header here</div>"}
                      tooltip={
                        "OPTIONAL: Inline HTML to render in the header section in place of the standard logo & title."
                      }
                    />
                    <TextAreaField
                      name={"custom_footer_html_inspire"}
                      label={"Custom Footer HTML"}
                      placeholder={"<div>Custom footer here</div>"}
                      tooltip={
                        "OPTIONAL: Inline HTML to render in the footer section in place of the standard logo & title."
                      }
                    />
                  </>
                )}
                {theme === StatusPageTheme.LEGACY && (
                  <>
                    <TextAreaField
                      name={"custom_css"}
                      label={"Custom CSS"}
                      placeholder={"/* Add custom styles here */"}
                      tooltip={"OPTIONAL: Inline CSS used to style customized HTML in the Header and Footer sections."}
                    />
                    <TextAreaField
                      name={"custom_header_html"}
                      label={"Custom Header HTML"}
                      placeholder={"<div>Custom header here</div>"}
                      tooltip={
                        "OPTIONAL: Inline HTML to render in the header section in place of the standard logo & title."
                      }
                    />
                    <TextAreaField
                      name={"custom_footer_html"}
                      label={"Custom Footer HTML"}
                      placeholder={"<div>Custom footer here</div>"}
                      tooltip={
                        "OPTIONAL: Inline HTML to render in the footer section in place of the standard logo & title."
                      }
                    />
                  </>
                )}
              </>
            )}
            <ImageInputField
              name="email_logo"
              label="Custom Email Logo"
              helpText="Used in subscriber notification emails. 200×50px recommended."
            />
          </div>
        </Section>
      </div>
      <div className={styles.previewColumn}>
        <DesignPreviewPane />
      </div>
    </div>
  );
};
